/**
 * Convert between the stored phrase form (with the `{name}` token) and the
 * name-resolved form shown to caregivers.
 *
 * General-population caregivers have no "template" mental model, so the Voice
 * settings never show them `{name}`. Phrase inputs display the elder's real
 * name and convert it back to `{name}` on save, so a later rename still
 * re-personalises the phrase.
 *
 * The reverse match (name → token) is whole-word + case-insensitive: an elder
 * named "Eve" is tokenised, but "evening" / "believe" are left untouched. A
 * "word" char here is any Unicode letter or number, so accented / non-Latin
 * names ("José", "Zoë") tokenise too — plain `\b` would miss them. `{` and `}`
 * also count as boundaries, so a name like "Time" never eats the `{time}`
 * token.
 *
 * Residual limitation: a first name that is also a common word (Dawn, Grace,
 * Bill) used non-nominally in a phrase only misfires if the elder is later
 * renamed — the spoken output is unchanged until then.
 *
 * Names shorter than 2 characters aren't substituted at all (too
 * collision-prone) — and both directions honour that, so a 1-char name shows
 * the raw {name} token rather than silently baking the literal name in.
 */

export const NAME_TOKEN = '{name}';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Whole-"word" match for the name. The leading boundary is a *captured*
 * character (start-of-string or a non-letter/number/brace) rather than a
 * lookbehind, so this runs on older Safari; the trailing boundary is a
 * lookahead. `{`/`}` are boundaries so we never match inside an existing
 * `{...}` token. `u` flag makes `\p{L}`/`\p{N}` Unicode-aware. */
function nameRegExp(name: string): RegExp {
  return new RegExp(`(^|[^\\p{L}\\p{N}{])(${escapeRegExp(name)})(?![\\p{L}\\p{N}}])`, 'giu');
}

/** True when a name is safe/worth substituting. Applied in BOTH directions so
 * the display and stored forms stay in sync for degenerate names. */
function canSubstitute(name: string): boolean {
  return name.trim().length >= 2;
}

/** Storage form ("Hello {name}.") → what the caregiver sees ("Hello Ruth."). */
export function phraseToDisplay(stored: string, elderName: string): string {
  if (!canSubstitute(elderName)) return stored;
  return stored.split(NAME_TOKEN).join(elderName.trim());
}

/** What the caregiver typed ("Hi Ruth!") → storage form ("Hi {name}!"). */
export function phraseToStored(display: string, elderName: string): string {
  if (!canSubstitute(elderName)) return display;
  return display.replace(nameRegExp(elderName.trim()), (_m, pre) => `${pre}${NAME_TOKEN}`);
}
